// @ts-nocheck
/* eslint-disable */
import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import type { Nurse } from '../lib/types'
import {
  Plus, Trash2, X, Check, Loader2, AlertCircle,
  FileSpreadsheet, Download, ChevronLeft, ChevronRight, Euro, Settings,
  Building2, Home, RefreshCw, FlaskConical,
  TrendingUp, BarChart3, Users, Clock, ArrowUpDown, PieChart, LayoutDashboard, FileText, Wrench, CalendarRange
} from 'lucide-react'

// @ts-ignore
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

interface Access {
  id: string
  nurse_id: string
  data: string
  tipo: string
  ore: number
  costo_orario: number
  km_percorsi: number
  rimborso_km: number
  rimborso_extra: number
  descrizione?: string
  importo_fisso?: number
  note?: string
  created_at?: string
}

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre']

const TIPI_PRESTAZIONE = [
  { key: 'hospital_meeting', label: 'Hospital Meeting', icon: '🏥', color: 'purple', adOre: true, defaultRate: 50 },
  { key: 'primo_accesso', label: 'Primo Accesso', icon: '🏠', color: 'blue', adOre: true, defaultRate: 50 },
  { key: 'accessi_successivi', label: 'Accesso Successivo', icon: '🔄', color: 'emerald', adOre: true, defaultRate: 40 },
  { key: 'training_farmaceutica', label: 'Training Farmaceutica', icon: '💊', color: 'amber', adOre: true, defaultRate: 50 },
] as const

type TipoKey = typeof TIPI_PRESTAZIONE[number]['key']

const TIPO_STYLES: Record<string, string> = {
  hospital_meeting: 'text-purple-700 bg-purple-50 border-purple-200',
  primo_accesso: 'text-blue-700 bg-blue-50 border-blue-200',
  accessi_successivi: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  training_farmaceutica: 'text-amber-700 bg-amber-50 border-amber-200',
}

const TIPO_ICONS: Record<string, any> = {
  hospital_meeting: Building2,
  primo_accesso: Home,
  accessi_successivi: RefreshCw,
  training_farmaceutica: FlaskConical,
}

const TIPO_LABELS: Record<string, string> = {
  hospital_meeting: 'Hospital Meeting',
  primo_accesso: 'Primo Accesso',
  accessi_successivi: 'Acc. Successivo',
  training_farmaceutica: 'Training Farm.',
}

const CARD_COLORS: Record<string, { bg: string; border: string; text: string; icon: string }> = {
  hospital_meeting: { bg: 'from-purple-50 to-violet-50', border: 'border-purple-200', text: 'text-purple-800', icon: 'text-purple-500' },
  primo_accesso: { bg: 'from-blue-50 to-indigo-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-500' },
  accessi_successivi: { bg: 'from-emerald-50 to-green-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: 'text-emerald-500' },
  training_farmaceutica: { bg: 'from-amber-50 to-orange-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-500' },
}

function GlobalBillingOverview({ accesses, nurses, rates, month, year, dateMode, dateFrom, dateTo, onSelectNurse }: { accesses: Access[], nurses: Nurse[], rates: Record<string, number>, month: number, year: number, dateMode: 'month' | 'range', dateFrom: string, dateTo: string, onSelectNurse: (id: string) => void }) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  useEffect(() => { setSelectedIds(new Set(nurses.map((n: Nurse) => n.id))) }, [nurses])
  
  const stats = useMemo(() => {
    let subtotale = 0
    let rimborsiKm = 0
    let rimborsiExtra = 0
    let enpapi4 = 0
    let ritenuta20 = 0
    let byNurse: Record<string, { nurse: Nurse, count: number, subtotale: number, rimborsi: number, isPiva: boolean }> = {}

    accesses.forEach((a: Access) => {
      const nurse = nurses.find((n: Nurse) => n.id === a.nurse_id)
      if (!nurse) return

      const isPiva = !(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta')
      const importo = (a.ore || 1) * (a.costo_orario || rates[a.tipo] || 0)
      const accessTotal = importo + (a.rimborso_km || 0) + (a.rimborso_extra || 0)

      subtotale += importo
      rimborsiKm += (a.rimborso_km || 0)
      rimborsiExtra += (a.rimborso_extra || 0)

      if (isPiva) {
        enpapi4 += importo * 0.04
      } else {
        ritenuta20 += importo * 0.20
      }

      if (!byNurse[nurse.id]) { byNurse[nurse.id] = { nurse, count: 0, subtotale: 0, rimborsi: 0, isPiva } }
      byNurse[nurse.id].count++
      byNurse[nurse.id].subtotale += importo
      byNurse[nurse.id].rimborsi += (a.rimborso_km || 0) + (a.rimborso_extra || 0)
    })
    return { subtotale, rimborsiKm, rimborsiExtra, enpapi4, ritenuta20, baseCompute: subtotale + rimborsiKm + rimborsiExtra, byNurse: Object.values(byNurse).sort((a: { subtotale: number }, b: { subtotale: number }) => b.subtotale - a.subtotale) }
  }, [accesses, nurses, rates])

  async function handleExportMasterExcel() {
    if (selectedIds.size === 0) { alert('Seleziona almeno un infermiere da esportare.'); return }
    const wb = new ExcelJS.Workbook()
    wb.creator = 'QuiCare Srl'
    wb.created = new Date()
    const periodo = dateMode === 'range'
      ? `${new Date(dateFrom).toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'numeric'})} - ${new Date(dateTo).toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'numeric'})}`
      : `${MESI[month]} ${year}`
    const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })

    const thinBorder: any = { top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, left: { style: 'thin', color: { argb: 'FFE2E8F0' } }, bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } }, right: { style: 'thin', color: { argb: 'FFE2E8F0' } } }
    function styleRow(ws: any, rowNum: number, colsCount: number, styles: any) {
      const row = ws.getRow(rowNum)
      for (let c = 1; c <= colsCount; c++) {
        const cell = row.getCell(c)
        if (styles.font) cell.font = styles.font
        if (styles.fill) cell.fill = styles.fill
        if (styles.alignment) cell.alignment = styles.alignment
        if (styles.border) cell.border = styles.border
      }
    }

    const TIPO_EMOJIS: Record<string, string> = { hospital_meeting: '\ud83c\udfe5', primo_accesso: '\ud83c\udfe0', accessi_successivi: '\ud83d\udd04', training_farmaceutica: '\ud83d\udc8a' }

    const selectedNursesData = stats.byNurse.filter((n: any) => selectedIds.has(n.nurse.id))

    // Calcoli globali
    let gtSubtotale = 0, gtRimborsi = 0, gtLordo = 0, gtEnpapi = 0, gtRit = 0, gtFinale = 0, gtAccessi = 0
    selectedNursesData.forEach((n: any) => {
      const lordo = n.subtotale + n.rimborsi
      const enpapi = n.isPiva ? n.subtotale * 0.04 : 0
      const rit = n.isPiva ? 0 : n.subtotale * 0.20
      const final_ = n.isPiva ? lordo + enpapi : lordo - rit
      gtSubtotale += n.subtotale; gtRimborsi += n.rimborsi; gtLordo += lordo
      gtEnpapi += enpapi; gtRit += rit; gtFinale += final_; gtAccessi += n.count
    })

    // ==================================================================
    // FOGLIO 1: MASTER RIEPILOGO
    // ==================================================================
    const ws = wb.addWorksheet('Master Riepilogo', { properties: { tabColor: { argb: 'FF1E1B4B' } } })
    const cols = 10
    ws.columns = [
      { width: 4 }, { width: 28 }, { width: 14 }, { width: 10 },
      { width: 14 }, { width: 14 }, { width: 14 }, { width: 20 },
      { width: 18 }, { width: 10 }
    ]

    // Nascondiamo le gridlines standard per un effetto Dashboard
    ws.views = [{ showGridLines: false, zoomScale: 90 }]

    // Row 1: Header aziendale
    ws.mergeCells('A1:J1'); ws.getRow(1).height = 60
    const banner = ws.getRow(1).getCell(1)
    banner.value = 'QuiCare Srl'
    banner.font = { size: 28, bold: true, color: { argb: 'FFFFFFFF' } }
    banner.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(ws, 1, cols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } } })

    // Row 2: Titolo periodo
    ws.mergeCells('A2:J2'); ws.getRow(2).height = 36
    const sub = ws.getRow(2).getCell(1)
    sub.value = `MASTER FATTURAZIONE \u2014 ${periodo.toUpperCase()}`
    sub.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }
    sub.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(ws, 2, cols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } } })

    // Row 3: Info ribbon
    ws.mergeCells('A3:J3'); ws.getRow(3).height = 28
    const info = ws.getRow(3).getCell(1)
    info.value = `${selectedNursesData.length} operatori   \u00b7   ${gtAccessi} accessi totali   \u00b7   Generato il ${today}`
    info.font = { size: 11, italic: true, color: { argb: 'FF312E81' } }
    info.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(ws, 3, cols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } } })

    // Row 4: spacer
    ws.getRow(4).height = 8

    // Row 5-6: KPI cards (two separate rows like the mockup: big value on top, label below)
    ws.getRow(5).height = 70
    ws.getRow(6).height = 28
    const kpiDefs = [
      { label: 'TOTALE AZIENDALE', value: `\u20ac ${gtFinale.toLocaleString('it-IT', {minimumFractionDigits:2, maximumFractionDigits:2})}`, color: 'FF1E1B4B', cols: [1, 4] },
      { label: 'ACCESSI TOTALI', value: `${gtAccessi}`, color: 'FF4F46E5', cols: [5, 7] },
      { label: 'OPERATORI', value: `${selectedNursesData.length}`, color: 'FF059669', cols: [8, 10] },
    ]
    kpiDefs.forEach(kpi => {
      // Value row (row 5)
      ws.mergeCells(5, kpi.cols[0], 5, kpi.cols[1])
      const vCell = ws.getRow(5).getCell(kpi.cols[0])
      vCell.value = kpi.value
      vCell.font = { size: 28, bold: true, color: { argb: 'FFFFFFFF' } }
      vCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } }
      vCell.alignment = { vertical: 'middle', horizontal: 'center' }
      for (let c = kpi.cols[0]; c <= kpi.cols[1]; c++) {
        const cc = ws.getRow(5).getCell(c)
        cc.fill = vCell.fill
        cc.border = { top: { style: 'thick', color: { argb: 'FFFFFFFF' } }, left: { style: 'thick', color: { argb: 'FFFFFFFF' } }, bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } }, right: { style: 'thick', color: { argb: 'FFFFFFFF' } } }
      }

      // Label row (row 6)
      ws.mergeCells(6, kpi.cols[0], 6, kpi.cols[1])
      const lCell = ws.getRow(6).getCell(kpi.cols[0])
      lCell.value = kpi.label
      lCell.font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } }
      lCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } }
      lCell.alignment = { vertical: 'top', horizontal: 'center' }
      for (let c = kpi.cols[0]; c <= kpi.cols[1]; c++) {
        const cc = ws.getRow(6).getCell(c)
        cc.fill = lCell.fill
        cc.border = { top: { style: 'thin', color: { argb: 'FFFFFFFF' } }, left: { style: 'thick', color: { argb: 'FFFFFFFF' } }, bottom: { style: 'thick', color: { argb: 'FFFFFFFF' } }, right: { style: 'thick', color: { argb: 'FFFFFFFF' } } }
      }
    })

    // Row 7: spacer
    ws.getRow(7).height = 8
    ws.getRow(8).height = 8

    // Row 9: Intestazioni tabella operatori
    const headers = ['#', 'Operatore', 'Contratto', 'Accessi', 'Subtotale', 'Rimborsi', 'Lordo', 'ENPAPI / Ritenuta', 'Netto/Fatturare', '% Totale']
    ws.getRow(9).height = 36
    headers.forEach((h, i) => {
      const cell = ws.getRow(9).getCell(i + 1)
      cell.value = h
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    })

    // Righe operatori (starting row 10)
    let r = 10
    const bottomSoftBorder = { bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } } }
    selectedNursesData.forEach((n: any, idx: number) => {
      const row = ws.getRow(r); row.height = 32
      const bg = idx % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF'
      const lordo = n.subtotale + n.rimborsi
      const enpapi = n.isPiva ? n.subtotale * 0.04 : 0
      const rit = n.isPiva ? 0 : n.subtotale * 0.20
      const final_ = n.isPiva ? lordo + enpapi : lordo - rit
      const pct = gtFinale > 0 ? `${Math.round((final_ / gtFinale) * 100)}%` : '0%'
      const enpRit = n.isPiva ? enpapi : -rit

      const vals: any[] = [idx + 1, `${n.nurse.cognome} ${n.nurse.nome}`, n.isPiva ? 'P.IVA' : 'Ritenuta', n.count, n.subtotale, n.rimborsi, lordo, enpRit, final_, pct]
      vals.forEach((v: any, j: number) => {
        const cell = row.getCell(j + 1)
        cell.value = (typeof v === 'number' && v === 0) ? '' : v
        cell.font = { size: 12, bold: j === 1 || j === 8, color: { argb: 'FF334155' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
        cell.border = bottomSoftBorder
        cell.alignment = { vertical: 'middle', horizontal: j <= 2 ? 'left' : 'center' }
        if (typeof v === 'number' && j >= 4 && j <= 8) cell.numFmt = '\u20ac #,##0.00'
      })
      // Colora contratto
      const cCell = row.getCell(3)
      cCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      cCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: n.isPiva ? 'FF6366F1' : 'FFD97706' } }
      cCell.alignment = { vertical: 'middle', horizontal: 'center' }
      r++
    })

    // Riga vuota
    r++

    // Riga TOTALE AZIENDALE
    const totRow = ws.getRow(r); totRow.height = 36
    for (let c = 1; c <= cols; c++) {
      const cell = totRow.getCell(c)
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } }
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center' }
    }
    totRow.getCell(2).value = 'TOTALE AZIENDALE'
    totRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' }
    totRow.getCell(4).value = gtAccessi
    totRow.getCell(5).value = gtSubtotale; totRow.getCell(5).numFmt = '\u20ac #,##0.00'
    totRow.getCell(6).value = gtRimborsi; totRow.getCell(6).numFmt = '\u20ac #,##0.00'
    totRow.getCell(7).value = gtLordo; totRow.getCell(7).numFmt = '\u20ac #,##0.00'
    totRow.getCell(8).value = gtEnpapi - gtRit; totRow.getCell(8).numFmt = '\u20ac #,##0.00'
    totRow.getCell(9).value = gtFinale; totRow.getCell(9).numFmt = '\u20ac #,##0.00'

    r += 2

    // RIEPILOGO FISCALE
    ws.mergeCells(`A${r}:J${r}`); ws.getRow(r).height = 26
    const fiscTitle = ws.getRow(r).getCell(1)
    fiscTitle.value = 'RIEPILOGO FISCALE'
    fiscTitle.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }
    fiscTitle.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(ws, r, cols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } } })

    r++
    const fiscalLines: [string, number, string][] = [
      ['Subtotale Prestazioni', gtSubtotale, 'FF4F46E5'],
      ['Rimborsi Km + Extra', gtRimborsi, 'FF2563EB'],
      ['Lordo Complessivo', gtLordo, 'FF475569'],
      ['ENPAPI 4% (P.IVA)', gtEnpapi, 'FF059669'],
      ["Ritenuta d'Acconto 20%", gtRit, 'FFDC2626'],
      ['TOTALE COSTO AZIENDALE', gtFinale, 'FF1E1B4B'],
    ]

    fiscalLines.forEach(([label, value, color], idx) => {
      const row = ws.getRow(r)
      const isLast = idx === fiscalLines.length - 1
      row.height = isLast ? 36 : 28

      ws.mergeCells(`A${r}:F${r}`)
      const lCell = row.getCell(1)
      lCell.value = `     ${label}`
      lCell.font = { size: isLast ? 14 : 12, bold: isLast, color: { argb: isLast ? 'FFFFFFFF' : 'FF1E293B' } }
      lCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isLast ? color : (idx % 2 === 0 ? 'FFF1F5F9' : 'FFFFFFFF') } }
      lCell.alignment = { vertical: 'middle', horizontal: 'left' }

      ws.mergeCells(`G${r}:J${r}`)
      const vCell = row.getCell(7)
      vCell.value = value
      vCell.numFmt = '\u20ac #,##0.00'
      vCell.font = { size: isLast ? 16 : 14, bold: true, color: { argb: isLast ? 'FFFFFFFF' : color } }
      vCell.fill = lCell.fill
      vCell.alignment = { vertical: 'middle', horizontal: 'right' }
      r++
    })

    // Footer
    r += 2
    ws.mergeCells(`A${r}:J${r}`)
    const footer = ws.getRow(r).getCell(1)
    footer.value = `QuiCare Srl \u2014 Documento riservato \u2014 Master Fatturazione ${periodo} \u2014 Generato il ${today}`
    footer.font = { size: 8, italic: true, color: { argb: 'FF94A3B8' } }
    footer.alignment = { horizontal: 'center' }

    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 9, showGridLines: false }]
    ws.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }

    // ==================================================================
    // FOGLIO 2: TARIFFARIO
    // ==================================================================
    const wsT = wb.addWorksheet('Tariffario', { properties: { tabColor: { argb: 'FF059669' } } })
    wsT.views = [{ showGridLines: false }]
    const tCols = 5
    wsT.columns = [{ width: 5 }, { width: 36 }, { width: 24 }, { width: 18 }, { width: 5 }]

    // Header verde
    wsT.mergeCells('A1:E1'); wsT.getRow(1).height = 40
    const tTitle = wsT.getRow(1).getCell(1)
    tTitle.value = 'TARIFFARIO PRESTAZIONI \u2014 QuiCare Srl'
    tTitle.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
    tTitle.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsT, 1, tCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } } })

    // Info ribbon
    wsT.mergeCells('A2:E2'); wsT.getRow(2).height = 22
    const tInfo = wsT.getRow(2).getCell(1)
    tInfo.value = `Aggiornato al ${today}  \u00b7  Tariffa km: \u20ac${(rates.tariffa_km || 0).toFixed(2)}/km`
    tInfo.font = { size: 9, italic: true, color: { argb: 'FF065F46' } }
    tInfo.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsT, 2, tCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } } })

    // Intestazioni
    const tHeaders = ['', 'Prestazione', 'Tipo Tariffa', 'Costo']
    wsT.getRow(3).height = 26
    tHeaders.forEach((h, i) => {
      const c = wsT.getRow(3).getCell(i + 1)
      c.value = h
      c.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF065F46' } }
      c.alignment = { vertical: 'middle', horizontal: 'center' }
    })

    // Righe tariffe
    TIPI_PRESTAZIONE.forEach((tp, idx) => {
      const row = wsT.getRow(4 + idx)
      row.height = 32
      const emoji = TIPO_EMOJIS[tp.key] || ''
      const bg = idx % 2 === 0 ? 'FFD1FAE5' : 'FFFFFFFF'
      const vals = ['', `${emoji}  ${tp.label}`, 'Tariffa oraria (\u20ac/h)', `\u20ac ${rates[tp.key].toFixed(2)}`]
      vals.forEach((v, j) => {
        const c = row.getCell(j + 1)
        c.value = v
        c.font = j === 1 ? { size: 12, bold: true, color: { argb: 'FF1E293B' } } : { size: 11, color: { argb: 'FF475569' } }
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
        c.alignment = { vertical: 'middle', horizontal: j <= 1 ? 'left' : 'center' }
        c.border = { bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } } }
      })
    })

    // Riga tariffa km
    const kmRow = wsT.getRow(4 + TIPI_PRESTAZIONE.length + 1); kmRow.height = 26
    wsT.mergeCells(4 + TIPI_PRESTAZIONE.length + 1, 2, 4 + TIPI_PRESTAZIONE.length + 1, 3)
    const kmLabel = kmRow.getCell(2)
    kmLabel.value = 'Tariffa Rimborso Chilometrico'
    kmLabel.font = { size: 11, bold: true, color: { argb: 'FF1E293B' } }
    kmLabel.alignment = { vertical: 'middle', horizontal: 'left' }
    const kmVal = kmRow.getCell(4)
    kmVal.value = `\u20ac ${(rates.tariffa_km || 0).toFixed(2)} / km`
    kmVal.font = { size: 11, bold: true, color: { argb: 'FF059669' } }
    kmVal.alignment = { vertical: 'middle', horizontal: 'center' }

    wsT.pageSetup = { orientation: 'portrait', fitToPage: true, fitToWidth: 1 }

    // ==================================================================
    // FOGLI 3+: DETTAGLIO PER OPERATORE
    // ==================================================================
    selectedNursesData.forEach((n: any) => {
      const shortName = `${n.nurse.cognome}_${n.nurse.nome}`.substring(0, 30)
      const wsN = wb.addWorksheet(shortName, { properties: { tabColor: { argb: n.isPiva ? 'FF4F46E5' : 'FFF59E0B' } } })
      wsN.views = [{ showGridLines: false, zoomScale: 90 }]
      wsN.columns = [{ width: 4 }, { width: 14 }, { width: 22 }, { width: 30 }, { width: 10 }, { width: 14 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 20 }, { width: 16 }]

      // Banner
      wsN.mergeCells('A1:L1'); wsN.getRow(1).height = 40
      const c1 = wsN.getRow(1).getCell(1)
      c1.value = `DETTAGLIO: ${n.nurse.cognome} ${n.nurse.nome} \u2014 ${periodo}`
      c1.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
      c1.alignment = { vertical: 'middle', horizontal: 'center' }
      styleRow(wsN, 1, 12, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: n.isPiva ? 'FF4F46E5' : 'FFF59E0B' } } })

      // Info ribbon
      wsN.mergeCells('A2:L2'); wsN.getRow(2).height = 28
      const c2 = wsN.getRow(2).getCell(1)
      c2.value = `${n.isPiva ? 'Partita IVA' : "Ritenuta d'Acconto"}   \u00b7   ${n.count} accessi   \u00b7   Subtotale \u20ac${n.subtotale.toFixed(2)}   \u00b7   Rimborsi \u20ac${n.rimborsi.toFixed(2)}`
      c2.font = { size: 11, italic: true, color: { argb: n.isPiva ? 'FF312E81' : 'FF92400E' } }
      c2.alignment = { vertical: 'middle', horizontal: 'center' }
      styleRow(wsN, 2, 12, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: n.isPiva ? 'FFE0E7FF' : 'FFFEF3C7' } } })

      // Headers
      const dHead = ['#', 'Data', 'Tipo', 'Descrizione', 'Ore/Acc', 'Importo', 'Km', 'Rimb. Km', 'Extra', n.isPiva ? 'ENPAPI' : 'Ritenuta', 'Note', 'Totale Linea']
      wsN.getRow(3).height = 28
      dHead.forEach((h, i) => {
        const hc = wsN.getRow(3).getCell(i + 1); hc.value = h
        hc.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } }
        hc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } }
        hc.alignment = { vertical: 'middle', horizontal: 'center' }
      })

      // Data rows
      const accs = accesses.filter((a: Access) => a.nurse_id === n.nurse.id).sort((a: Access, b: Access) => new Date(a.data).getTime() - new Date(b.data).getTime())
      let pr = 4
      const pSoftBorder = { bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } } }
      accs.forEach((a: Access, i: number) => {
        const row = wsN.getRow(pr); row.height = 26
        const imp = (a.ore || 1) * (a.costo_orario || rates[a.tipo] || 0)
        const tax = n.isPiva ? imp * 0.04 : imp * 0.20
        const final_ = n.isPiva ? (imp * 1.04 + (a.rimborso_km || 0) + (a.rimborso_extra || 0)) : (imp * 0.80 + (a.rimborso_km || 0) + (a.rimborso_extra || 0))
        const vals = [i + 1, new Date(a.data).toLocaleDateString('it-IT'), a.tipo.replace(/_/g, ' '), a.descrizione || '', `${a.ore || 1}h`, imp, a.km_percorsi || '', a.rimborso_km || '', a.rimborso_extra || '', tax, a.note || '', final_]
        const bg = i % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF'
        vals.forEach((v, j) => {
          const cell = row.getCell(j + 1)
          cell.value = (typeof v === 'number' && v === 0) ? '' : v
          cell.font = { size: 10, bold: j === 11, color: { argb: j === 11 ? 'FF1E1B4B' : 'FF334155' } }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          cell.border = pSoftBorder
          cell.alignment = { vertical: 'middle', horizontal: j <= 3 || j === 10 ? 'left' : 'center' }
          if (typeof v === 'number' && typeof vals[j] === 'number' && j >= 5) cell.numFmt = '\u20ac #,##0.00'
        })
        pr++
      })

      // Totals
      const tRow = wsN.getRow(pr); tRow.height = 28
      for (let c = 1; c <= 12; c++) {
        const cell = tRow.getCell(c)
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } }
        cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { top: { style: 'medium', color: { argb: 'FF1E1B4B' } } }
      }
      tRow.getCell(1).value = 'TOTALE'; tRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' }
      const nLordo = n.subtotale + n.rimborsi
      const nEnpapi = n.isPiva ? n.subtotale * 0.04 : 0
      const nRit = n.isPiva ? 0 : n.subtotale * 0.20
      const nFinal = n.isPiva ? nLordo + nEnpapi : nLordo - nRit
      tRow.getCell(6).value = n.subtotale; tRow.getCell(6).numFmt = '\u20ac #,##0.00'
      tRow.getCell(8).value = n.rimborsi; tRow.getCell(8).numFmt = '\u20ac #,##0.00'
      tRow.getCell(10).value = n.isPiva ? nEnpapi : nRit; tRow.getCell(10).numFmt = '\u20ac #,##0.00'
      tRow.getCell(12).value = nFinal; tRow.getCell(12).numFmt = '\u20ac #,##0.00'

      // Riepilogo fiscale individuale
      pr += 2
      wsN.mergeCells(`A${pr}:L${pr}`); wsN.getRow(pr).height = 26
      const fTitle = wsN.getRow(pr).getCell(1)
      fTitle.value = `RIEPILOGO FISCALE \u2014 ${n.nurse.cognome} ${n.nurse.nome}`
      fTitle.font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } }
      fTitle.alignment = { vertical: 'middle', horizontal: 'center' }
      styleRow(wsN, pr, 12, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: n.isPiva ? 'FF4F46E5' : 'FFF59E0B' } } })

      pr++
      const nFiscalLines: [string, number, string][] = n.isPiva ? [
        ['Subtotale Prestazioni', n.subtotale, 'FF4F46E5'],
        ['Rimborsi (Km + Extra)', n.rimborsi, 'FF2563EB'],
        ['Base Imponibile', nLordo, 'FF475569'],
        ['ENPAPI 4%', nEnpapi, 'FF059669'],
        ['TOTALE DA FATTURARE', nFinal, 'FF1E1B4B'],
      ] : [
        ['Subtotale Prestazioni', n.subtotale, 'FF4F46E5'],
        ['Rimborsi (Km + Extra)', n.rimborsi, 'FF2563EB'],
        ['Base Imponibile', nLordo, 'FF475569'],
        ["Ritenuta d'Acconto 20%", -nRit, 'FFDC2626'],
        ['NETTO A PAGARE', nFinal, 'FF1E1B4B'],
      ]

      nFiscalLines.forEach(([label, value, color], idx) => {
        const fRow = wsN.getRow(pr)
        const isLast = idx === nFiscalLines.length - 1
        fRow.height = isLast ? 28 : 22

        wsN.mergeCells(`C${pr}:H${pr}`)
        const lC = fRow.getCell(3)
        lC.value = label
        lC.font = { size: isLast ? 11 : 10, bold: isLast, color: { argb: isLast ? 'FFFFFFFF' : 'FF1E293B' } }
        lC.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isLast ? color : (idx % 2 === 0 ? 'FFF1F5F9' : 'FFFFFFFF') } }
        lC.alignment = { vertical: 'middle', horizontal: 'left' }
        lC.border = thinBorder

        wsN.mergeCells(`I${pr}:L${pr}`)
        const vC = fRow.getCell(9)
        vC.value = value
        vC.numFmt = '\u20ac #,##0.00'
        vC.font = { size: isLast ? 13 : 11, bold: true, color: { argb: isLast ? 'FFFFFFFF' : color } }
        vC.fill = lC.fill
        vC.alignment = { vertical: 'middle', horizontal: 'right' }
        vC.border = thinBorder
        pr++
      })

      // Footer
      pr += 2
      wsN.mergeCells(`A${pr}:L${pr}`)
      const fCell = wsN.getRow(pr).getCell(1)
      fCell.value = `QuiCare Srl \u2014 Documento riservato \u2014 ${n.nurse.cognome} ${n.nurse.nome} \u2014 ${periodo}`
      fCell.font = { size: 8, italic: true, color: { argb: 'FF94A3B8' } }
      fCell.alignment = { horizontal: 'center' }

      wsN.views = [{ state: 'frozen', xSplit: 0, ySplit: 3 }]
      wsN.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
    })

    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `Master_Fatturazione_${periodo.replace(' ', '_')}.xlsx`)
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="col-span-1 md:col-span-4 bg-gradient-to-r from-slate-800 to-indigo-900 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-sm font-bold text-indigo-300 uppercase tracking-widest mb-1">Riepilogo Aziendale • {dateMode === 'range' ? `${new Date(dateFrom + 'T00:00:00').toLocaleDateString('it-IT', {day:'2-digit',month:'short'})} \u2014 ${new Date(dateTo + 'T00:00:00').toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'numeric'})}` : `${MESI[month]} ${year}`}</div>
          <div className="flex items-end gap-4">
            <div className="text-5xl font-black">€{stats.baseCompute.toFixed(2)}</div>
            <div className="text-indigo-200 mb-1 text-sm">di cui {accesses.length} accessi erogati</div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-slate-200"><div className="text-xs font-bold text-slate-400 uppercase">Tot. Compensi Netti</div><div className="text-2xl font-black text-slate-700 mt-1">€{stats.subtotale.toFixed(2)}</div></div>
        <div className="bg-white p-5 rounded-2xl border border-slate-200"><div className="text-xs font-bold text-slate-400 uppercase">Tot. Rimborsi</div><div className="text-2xl font-black text-blue-600 mt-1">€{(stats.rimborsiKm + stats.rimborsiExtra).toFixed(2)}</div></div>
        <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200"><div className="text-xs font-bold text-emerald-600 uppercase">ENPAPI (P.IVA)</div><div className="text-2xl font-black text-emerald-700 mt-1">€{stats.enpapi4.toFixed(2)}</div></div>
        <div className="bg-red-50 p-5 rounded-2xl border border-red-200"><div className="text-xs font-bold text-red-600 uppercase">Ritenute 20%</div><div className="text-2xl font-black text-red-700 mt-1">€{stats.ritenuta20.toFixed(2)}</div></div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h3 className="font-black text-slate-700 uppercase tracking-widest text-xs">Dettaglio per Operatore</h3>
          <button onClick={handleExportMasterExcel} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-xs font-bold shadow-md transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline strokeLinecap="round" strokeLinejoin="round" points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline strokeLinecap="round" strokeLinejoin="round" points="10 9 9 9 8 9"></polyline></svg>
            Esporta Master Excel
          </button>
        </div>
        {stats.byNurse.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Nessun accesso registrato questo mese</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-slate-200 text-left text-xs font-bold text-slate-400 uppercase">
                <th className="px-5 py-3 w-10">
                  <input type="checkbox" checked={stats.byNurse.length > 0 && selectedIds.size === stats.byNurse.length} 
                    onChange={e => setSelectedIds(e.target.checked ? new Set(stats.byNurse.map((n: { nurse: Nurse, subtotale: number, rimborsi: number, isPiva: boolean, count: number }) => n.nurse.id)) : new Set())} 
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" 
                  />
                </th>
                <th className="px-5 py-3">Operatore</th>
                <th className="px-5 py-3">Contratto</th>
                <th className="px-5 py-3 text-center">Accessi</th>
                <th className="px-5 py-3 text-right">Rimborsi</th>
                <th className="px-5 py-3 text-right">Compenso Finale</th>
              </tr>
            </thead>
            <tbody>
              {stats.byNurse.map((n: { nurse: Nurse, subtotale: number, rimborsi: number, isPiva: boolean, count: number }) => {
                const totale = n.subtotale + n.rimborsi; const final = n.isPiva ? n.subtotale * 1.04 + n.rimborsi : n.subtotale * 0.80 + n.rimborsi
                const isChecked = selectedIds.has(n.nurse.id)
                return (
                  <tr key={n.nurse.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isChecked ? 'bg-indigo-50/20' : ''}`}>
                    <td className="px-5 py-4"><input type="checkbox" checked={isChecked} onChange={(e) => { const next = new Set(selectedIds); e.target.checked ? next.add(n.nurse.id) : next.delete(n.nurse.id); setSelectedIds(next) }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" /></td>
                    <td onClick={() => onSelectNurse(n.nurse.id)} className="px-5 py-4 font-bold text-slate-800 hover:text-amber-600 cursor-pointer flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-black">{n.nurse.nome[0]}{n.nurse.cognome[0]}</div>{n.nurse.cognome} {n.nurse.nome}</td>
                    <td className="px-5 py-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${n.isPiva ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>{n.isPiva ? 'P.IVA' : 'Ritenuta'}</span></td>
                    <td className="px-5 py-4 text-center font-bold text-slate-600">{n.count}</td>
                    <td className="px-5 py-4 text-right text-slate-600">€{n.rimborsi.toFixed(2)}</td>
                    <td className="px-5 py-4 text-right font-black text-slate-800">€{final.toFixed(2)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export function BillingPage() {
  const { canEditArchive: isAdmin } = useAuth()
  const [nurses, setNurses] = useState<Nurse[]>([])
  const [accesses, setAccesses] = useState<Access[]>([])
  const [selectedNurse, setSelectedNurse] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [dateMode, setDateMode] = useState<'month' | 'range'>('month')
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` })
  const [dateTo, setDateTo] = useState(() => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}` })
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showTrash, setShowTrash] = useState(false)
  const [billingTab, setBillingTab] = useState<'cruscotto' | 'compensi' | 'report' | 'config'>('cruscotto')
  const [sortColumn, setSortColumn] = useState<string>('subtotale')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const defaultRates: Record<string, number> = {
    hospital_meeting: 50, primo_accesso: 50, accessi_successivi: 40, training_farmaceutica: 50, tariffa_km: 0,
  }
  const [rates, setRates] = useState(defaultRates)

  const emptyForm: Partial<Access> = {
    data: new Date().toISOString().slice(0, 10),
    tipo: 'primo_accesso', ore: 1, costo_orario: rates.primo_accesso,
    km_percorsi: 0, rimborso_km: 0, rimborso_extra: 0, descrizione: '', note: '',
  }
  const [formData, setFormData] = useState<Partial<Access>>(emptyForm)

  useEffect(() => {
    const saved = localStorage.getItem('quicare_billing_rates_v2')
    if (saved) { try { setRates({ ...defaultRates, ...JSON.parse(saved) }) } catch {} }
    fetchData()
  }, [])

  useEffect(() => { fetchAccesses() }, [selectedNurse, selectedMonth, selectedYear, dateMode, dateFrom, dateTo])

  function saveRates(newRates: Record<string, number>) {
    setRates(newRates)
    localStorage.setItem('quicare_billing_rates_v2', JSON.stringify(newRates))
    setShowSettings(false)
    setSuccess('Tariffe aggiornate!')
  }

  async function fetchData() {
    setIsLoading(true)
    const { data } = await supabase.from('nurses').select('*').order('cognome')
    if (data) setNurses(data as Nurse[])
    setIsLoading(false)
  }

  async function fetchAccesses() {
    let startDate: string, endDate: string
    if (dateMode === 'range') {
      startDate = dateFrom
      // dateTo è inclusivo, quindi aggiungiamo un giorno per lt()
      const dtEnd = new Date(dateTo); dtEnd.setDate(dtEnd.getDate() + 1)
      endDate = `${dtEnd.getFullYear()}-${String(dtEnd.getMonth() + 1).padStart(2, '0')}-${String(dtEnd.getDate()).padStart(2, '0')}`
    } else {
      startDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`
      endDate = selectedMonth === 11
        ? `${selectedYear + 1}-01-01`
        : `${selectedYear}-${String(selectedMonth + 2).padStart(2, '0')}-01`
    }
    
    let query = supabase
      .from('accesses').select('*')
      .gte('data', startDate).lt('data', endDate)
      .order('data', { ascending: true })
      
    if (selectedNurse) {
      query = query.eq('nurse_id', selectedNurse)
    }
    
    const { data } = await query
    if (data) setAccesses(data as Access[])
  }

  const nurse = useMemo(() => nurses.find(n => n.id === selectedNurse), [nurses, selectedNurse])

  const filteredAccesses = useMemo(() => {
    if (!searchQuery) return accesses
    const s = searchQuery.toLowerCase()
    return accesses.filter(a => 
      (a.descrizione || '').toLowerCase().includes(s) ||
      (a.tipo || '').toLowerCase().includes(s) ||
      (a.note || '').toLowerCase().includes(s)
    )
  }, [accesses, searchQuery])

  // Billing calcs per tipo
  const billing = useMemo(() => {
    const byTipo: Record<string, { count: number; ore: number; totale: number }> = {}
    TIPI_PRESTAZIONE.forEach(t => { byTipo[t.key] = { count: 0, ore: 0, totale: 0 } })

    accesses.forEach(a => {
      const tipo = a.tipo || 'primo_accesso'
      if (!byTipo[tipo]) byTipo[tipo] = { count: 0, ore: 0, totale: 0 }
      const tp = TIPI_PRESTAZIONE.find(t => t.key === tipo)
      const importo = (a.ore || 1) * (a.costo_orario || rates[tipo as TipoKey] || 0)
      byTipo[tipo].count++
      byTipo[tipo].ore += a.ore || 0
      byTipo[tipo].totale += importo
    })

    const subtotale = Object.values(byTipo).reduce((s, v) => s + v.totale, 0)
    const totaleKm = accesses.reduce((s, a) => s + (a.km_percorsi || 0), 0)
    const rimborsiKm = accesses.reduce((s, a) => s + (a.rimborso_km || 0), 0)
    const rimborsiExtra = accesses.reduce((s, a) => s + (a.rimborso_extra || 0), 0)
    const baseCompute = subtotale + rimborsiKm + rimborsiExtra
    const enpapi4 = subtotale * 0.04
    const totaleFatturare = baseCompute + enpapi4
    const ritenuta20 = subtotale * 0.20
    const nettoRitenuta = baseCompute - ritenuta20

    return { byTipo, subtotale, totaleKm, rimborsiKm, rimborsiExtra, baseCompute, enpapi4, totaleFatturare, ritenuta20, nettoRitenuta, totaleAccessi: accesses.length }
  }, [accesses, rates])

  function getImporto(a: Access): number {
    return (a.ore || 1) * (a.costo_orario || 0)
  }

  function handleTipoChange(tipo: string) {
    const tp = TIPI_PRESTAZIONE.find(t => t.key === tipo)
    const cost = rates[tipo as TipoKey] || 50
    setFormData({ ...formData, tipo, costo_orario: cost, ore: formData.ore || 1 })
  }

  async function handleSaveAccess() {
    if (!selectedNurse || !formData.data) { setError('Seleziona un operatore e una data'); return }
    setSaving(true); setError('')
    try {
      const { error: err } = await supabase.from('accesses').insert({ ...formData, nurse_id: selectedNurse })
      if (err) throw err
      setSuccess('Accesso registrato!')
      setShowForm(false)
      setFormData({ ...emptyForm, costo_orario: rates.primo_accesso })
      fetchAccesses()
    } catch (err: any) { setError(err.message || 'Errore nel salvataggio') }
    finally { setSaving(false) }
  }

  async function handleDeleteAccess(id: string) {
    if (!confirm('Eliminare questo accesso?')) return
    const { error: err } = await supabase.from('accesses').delete().eq('id', id)
    if (err) setError('Errore: ' + err.message)
    else { setSuccess('Eliminato'); fetchAccesses() }
  }

  function prevMonth() { if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1) } else setSelectedMonth(m => m - 1) }
  function nextMonth() { if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1) } else setSelectedMonth(m => m + 1) }

  const periodoLabel = dateMode === 'range'
    ? `${new Date(dateFrom + 'T00:00:00').toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'numeric'})} — ${new Date(dateTo + 'T00:00:00').toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'numeric'})}`
    : `${MESI[selectedMonth]} ${selectedYear}`
  const periodoFile = dateMode === 'range'
    ? `${dateFrom}_${dateTo}`
    : `${MESI[selectedMonth]}_${selectedYear}`

  // ━━━━━━━━━━━━━━━━━━ GLOBAL STATS FOR CRUSCOTTO ━━━━━━━━━━━━━━━━━━
  const globalStats = useMemo(() => {
    let fatturato = 0, totalOre = 0, totalAccessi = accesses.length
    const byNurse: Record<string, { nurse: Nurse, count: number, ore: number, subtotale: number, rimborsi: number, isPiva: boolean }> = {}
    const byTipo: Record<string, { count: number, ore: number, totale: number }> = {}
    TIPI_PRESTAZIONE.forEach(t => { byTipo[t.key] = { count: 0, ore: 0, totale: 0 } })

    accesses.forEach(a => {
      const imp = (a.ore || 1) * (a.costo_orario || rates[a.tipo] || 0)
      const rimb = (a.rimborso_km || 0) + (a.rimborso_extra || 0)
      fatturato += imp + rimb
      totalOre += a.ore || 1
      const tipo = a.tipo || 'primo_accesso'
      if (byTipo[tipo]) { byTipo[tipo].count++; byTipo[tipo].ore += a.ore || 1; byTipo[tipo].totale += imp }

      const nurse = nurses.find(n => n.id === a.nurse_id)
      if (nurse) {
        const isPiva = !(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta')
        if (!byNurse[nurse.id]) byNurse[nurse.id] = { nurse, count: 0, ore: 0, subtotale: 0, rimborsi: 0, isPiva }
        byNurse[nurse.id].count++
        byNurse[nurse.id].ore += a.ore || 1
        byNurse[nurse.id].subtotale += imp
        byNurse[nurse.id].rimborsi += rimb
      }
    })

    const nurseList = Object.values(byNurse).sort((a, b) => b.subtotale - a.subtotale)
    const mediaAccesso = totalAccessi > 0 ? fatturato / totalAccessi : 0
    const maxTipo = Object.entries(byTipo).sort((a, b) => b[1].totale - a[1].totale)
    const tipoTotal = Object.values(byTipo).reduce((s, v) => s + v.totale, 0)

    return { fatturato, totalOre, totalAccessi, byNurse: nurseList, byTipo, mediaAccesso, maxTipo, tipoTotal }
  }, [accesses, nurses, rates])

  // --- A.I. Audit Executive Summary Generator (Billing) ---
  async function generateBillingSummary(nurseData: Nurse, stats: any, accessi: Access[], type: string): Promise<string> {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) return "Sommario A.I. non disponibile (API Key mancante).";
    
    const leanData = {
      operatore: `${nurseData.cognome} ${nurseData.nome}`,
      contratto: nurseData.tipo_contratto || 'N/A',
      ore_totali: stats.totalOre || 0,
      subtotale: stats.subtotale || 0,
      km_totali: stats.totaleKm || 0,
      num_accessi: accessi.length
    };
    
    try {
      const promptText = `Sei l'AI revisore contabile HR di QuiCare. Analizza le metriche mensili di questo infermiere per rilevare pattern o anomalie.
Scrivi un breve "A.I. Audit Executive Summary" (circa 120 parole) formale in formato discorsivo per l'amministrazione, commentando il fatturato maturato, il volume di accessi e l'incidenza dei chilometri. Se le cifre sono normali, dichiara il report in regola. Niente markdown.
Dati: ${JSON.stringify(leanData)}`;
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }] }] })
      });
      if (!response.ok) { let eMsg = `Network error (${response.status})`; try { const eData = await response.json(); eMsg += `: ${eData.error?.message}`; } catch(e){} throw new Error(eMsg); }
      const data = await response.json();
      let aiText = data.candidates?.[0]?.content?.parts?.[0]?.text || "Nessun output.";
      return aiText.replace(/\*/g, '').trim();
    } catch {
      return "Errore A.I. interno.";
    }
  }

  // ━━━━━━━━━━━━━━━━━━ EXPORT EXCEL (PREMIUM) ━━━━━━━━━━━━━━━━━━
  async function handleExportExcel() {
    if (!nurse) return
    setSuccess('Esecuzione Audit A.I. per Excel in corso...');
    const periodo = periodoLabel
    const today = new Date().toLocaleDateString('it-IT')
    const wb = new ExcelJS.Workbook()
    wb.creator = 'QuiCare Srl'
    wb.created = new Date()
    const isPiva = !(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta')
    const nomeCompleto = `${nurse.cognome} ${nurse.nome}`
    const tipoLabel = isPiva ? 'Partita IVA' : "Ritenuta d'Acconto"

    // === HELPER: apply style to every cell in a row ===
    function styleRow(ws: any, rowNum: number, colCount: number, styles: any) {
      const row = ws.getRow(rowNum)
      for (let c = 1; c <= colCount; c++) {
        const cell = row.getCell(c)
        if (styles.font) cell.font = styles.font
        if (styles.fill) cell.fill = styles.fill
        if (styles.alignment) cell.alignment = styles.alignment
        if (styles.border) cell.border = styles.border
      }
    }

    const thinBorder = {
      top: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
    }

    const TIPO_EMOJIS: Record<string, string> = {
      hospital_meeting: '🏥', primo_accesso: '🏠', accessi_successivi: '🔄', training_farmaceutica: '💊',
    }

    // ══════════════════════════════════════════════════════════════
    // FOGLIO 1: RESOCONTO (Summary Premium)
    // ══════════════════════════════════════════════════════════════
    const wsSummary = wb.addWorksheet('Resoconto', { properties: { tabColor: { argb: 'FF1E1B4B' } } })
    wsSummary.views = [{ showGridLines: false, zoomScale: 90 }]
    wsSummary.columns = [
      { width: 4 }, { width: 28 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 4 }
    ]
    const sCols = 8

    // Row 1: Top banner
    wsSummary.mergeCells('A1:H1')
    wsSummary.getRow(1).height = 50
    const bannerCell = wsSummary.getRow(1).getCell(1)
    bannerCell.value = 'QuiCare Srl'
    bannerCell.font = { size: 24, bold: true, color: { argb: 'FFFFFFFF' } }
    bannerCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsSummary, 1, sCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } } })

    // Row 2: Subtitle
    wsSummary.mergeCells('A2:H2')
    wsSummary.getRow(2).height = 30
    const subCell = wsSummary.getRow(2).getCell(1)
    subCell.value = `FATTURAZIONE PRESTAZIONI — ${periodo}`
    subCell.font = { size: 11, bold: true, color: { argb: 'FFFFFFFF' } }
    subCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsSummary, 2, sCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } } })

    // Row 3: Info ribbon
    wsSummary.mergeCells('A3:H3')
    wsSummary.getRow(3).height = 24
    const infoCell = wsSummary.getRow(3).getCell(1)
    infoCell.value = `👤  ${nomeCompleto}  ·  ${tipoLabel}  ·  ${nurse.regione || ''} - ${nurse.provincia || ''}  ·  ${accesses.length} prestazioni nel mese`
    infoCell.font = { size: 10, italic: true, color: { argb: 'FF4338CA' } }
    infoCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsSummary, 3, sCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } } })

    // Spacer
    wsSummary.getRow(4).height = 10

    // Row 5: A.I. Executive Summary Title
    wsSummary.mergeCells('B5:G5')
    wsSummary.getRow(5).height = 24
    const aiTitle = wsSummary.getRow(5).getCell(2)
    aiTitle.value = '✨ AUDIT MENSILE A.I.'
    aiTitle.font = { size: 12, bold: true, color: { argb: 'FF4F46E5' } }
    aiTitle.alignment = { vertical: 'middle' }

    // Row 6-9: A.I. Executive Summary Text
    wsSummary.mergeCells('B6:G9')
    const aiBody = wsSummary.getRow(6).getCell(2)
    aiBody.value = await generateBillingSummary(nurse, billing, accesses, 'Excel')
    aiBody.font = { size: 10, italic: true, color: { argb: 'FF475569' } }
    aiBody.alignment = { vertical: 'top', horizontal: 'left', wrapText: true }
    aiBody.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }
    aiBody.border = { top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } }, left: { style: 'thin', color: { argb: 'FFE2E8F0' } }, right: { style: 'thin', color: { argb: 'FFE2E8F0' } } }

    // Spacer
    wsSummary.getRow(10).height = 15

    // Row 11: KPI Title
    wsSummary.mergeCells('B11:G11')
    wsSummary.getRow(11).height = 28
    const kpiTitle = wsSummary.getRow(11).getCell(2)
    kpiTitle.value = '📊 RIEPILOGO INDICATORI'
    kpiTitle.font = { size: 13, bold: true, color: { argb: 'FF1E1B4B' } }

    // KPI boxes
    const kpis = [
      { label: 'Prestazioni Totali', value: accesses.length.toString(), color: 'FF4F46E5', icon: '📅' },
      { label: isPiva ? 'Subtotale + ENPAPI' : 'Subtotale Lordo', value: `€${billing.baseCompute.toFixed(0)}`, color: 'FF059669', icon: '💰' },
      { label: isPiva ? 'ENPAPI 4%' : 'Ritenuta 20%', value: isPiva ? `€${billing.enpapi4.toFixed(0)}` : `- €${billing.ritenuta20.toFixed(0)}`, color: isPiva ? 'FF7C3AED' : 'FFDC2626', icon: isPiva ? '🏦' : '✂️' },
      { label: 'Rimborso Km', value: `€${billing.rimborsiKm.toFixed(0)}`, color: 'FF2563EB', icon: '🚗' },
      { label: isPiva ? 'TOTALE FATTURA' : 'NETTO A PAGARE', value: isPiva ? `€${billing.totaleFatturare.toFixed(0)}` : `€${billing.nettoRitenuta.toFixed(0)}`, color: 'FF1E1B4B', icon: '🏆' },
      { label: 'Extra', value: `€${billing.rimborsiExtra.toFixed(0)}`, color: 'FFF59E0B', icon: '⭐' },
    ]

    let kpiRow = 12
    for (let i = 0; i < kpis.length; i += 3) {
      const row = wsSummary.getRow(kpiRow)
      row.height = 40
      for (let j = 0; j < 3 && i + j < kpis.length; j++) {
        const kpi = kpis[i + j]
        const col = 2 + j * 2
        const cell = row.getCell(col)
        cell.value = `${kpi.icon}  ${kpi.value}`
        cell.font = { size: 18, bold: true, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { top: { style: 'thin', color: { argb: 'FFFFFFFF' } }, left: { style: 'thin', color: { argb: 'FFFFFFFF' } }, bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } }, right: { style: 'thin', color: { argb: 'FFFFFFFF' } } }
        const labelCell = row.getCell(col + 1)
        labelCell.value = kpi.label
        labelCell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.color } }
        labelCell.alignment = { vertical: 'middle', horizontal: 'left' }
        labelCell.border = cell.border
      }
      kpiRow++
    }

    kpiRow++

    // Breakdown per tipo prestazione
    wsSummary.mergeCells(`B${kpiRow}:G${kpiRow}`)
    wsSummary.getRow(kpiRow).height = 28
    const breakTitle = wsSummary.getRow(kpiRow).getCell(2)
    breakTitle.value = '\ud83c\udfe5  DETTAGLIO PER TIPO PRESTAZIONE'
    breakTitle.font = { size: 13, bold: true, color: { argb: 'FF1E1B4B' } }

    const breakHeaderRow = kpiRow + 1
    wsSummary.getRow(breakHeaderRow).height = 26
    const breakHeaders = ['', 'Prestazione', 'Quantit\u00e0', 'Ore', 'Tariffa', '% su Totale']
    breakHeaders.forEach((h, i) => {
      const cell = wsSummary.getRow(breakHeaderRow).getCell(i + 2)
      cell.value = h
      cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } }
      cell.alignment = { vertical: 'middle', horizontal: i >= 2 ? 'center' : 'left' }
      cell.border = { bottom: { style: 'medium', color: { argb: 'FF0F172A' } } }
    })

    let bRow = breakHeaderRow + 1
    TIPI_PRESTAZIONE.forEach((tp, idx) => {
      const stats = billing.byTipo[tp.key] || { count: 0, ore: 0, totale: 0 }
      const row = wsSummary.getRow(bRow)
      row.height = 26
      const pct = billing.subtotale > 0 ? `${Math.round((stats.totale / billing.subtotale) * 100)}%` : '0%'
      const emoji = TIPO_EMOJIS[tp.key] || '\ud83d\udccc'
      const bgColor = idx % 2 === 0 ? 'FFF1F5F9' : 'FFFFFFFF'
      const values = ['', `${emoji}  ${tp.label}`, stats.count, `${stats.ore}h`, `\u20ac${rates[tp.key]}/h`, pct]
      values.forEach((v, i) => {
        const cell = row.getCell(i + 2)
        cell.value = v
        cell.font = i === 1 ? { size: 10, bold: true, color: { argb: 'FF1E293B' } } : { size: 10, color: { argb: 'FF475569' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
        cell.alignment = { vertical: 'middle', horizontal: i >= 2 ? 'center' : 'left' }
        cell.border = thinBorder
      })
      bRow++
    })

    // Fiscal summary block
    bRow++
    wsSummary.mergeCells(`B${bRow}:G${bRow}`)
    wsSummary.getRow(bRow).height = 28
    const fiscalTitle = wsSummary.getRow(bRow).getCell(2)
    fiscalTitle.value = `\ud83d\udcb3  RIEPILOGO FISCALE (${tipoLabel})`
    fiscalTitle.font = { size: 13, bold: true, color: { argb: 'FF1E1B4B' } }

    bRow++
    const fiscalLines = isPiva ? [
      ['Subtotale Prestazioni', billing.subtotale, 'FF4F46E5'],
      ['Rimborso Km', billing.rimborsiKm, 'FF2563EB'],
      ['Extra', billing.rimborsiExtra, 'FFF59E0B'],
      ['Base Imponibile', billing.baseCompute, 'FF475569'],
      ['ENPAPI 4%', billing.enpapi4, 'FF059669'],
      ['TOTALE DA FATTURARE', billing.totaleFatturare, 'FF1E1B4B'],
    ] : [
      ['Subtotale Prestazioni', billing.subtotale, 'FF4F46E5'],
      ['Rimborso Km', billing.rimborsiKm, 'FF2563EB'],
      ['Extra', billing.rimborsiExtra, 'FFF59E0B'],
      ['Base Imponibile', billing.baseCompute, 'FF475569'],
      ["Ritenuta d'Acconto 20%", -billing.ritenuta20, 'FFDC2626'],
      ['NETTO A PAGARE', billing.nettoRitenuta, 'FF1E1B4B'],
    ]

    fiscalLines.forEach(([label, value, color], idx) => {
      const row = wsSummary.getRow(bRow)
      row.height = idx === fiscalLines.length - 1 ? 32 : 24
      const isLast = idx === fiscalLines.length - 1

      const lCell = row.getCell(3)
      lCell.value = label as string
      lCell.font = { size: isLast ? 12 : 10, bold: isLast, color: { argb: isLast ? 'FFFFFFFF' : 'FF1E293B' } }
      lCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isLast ? (color as string) : (idx % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF') } }
      lCell.alignment = { vertical: 'middle', horizontal: 'left' }

      wsSummary.mergeCells(`D${bRow}:E${bRow}`)
      const vCell = row.getCell(4)
      vCell.value = value as number
      vCell.numFmt = '\u20ac #,##0.00'
      vCell.font = { size: isLast ? 14 : 11, bold: true, color: { argb: isLast ? 'FFFFFFFF' : (color as string) } }
      vCell.fill = lCell.fill
      vCell.alignment = { vertical: 'middle', horizontal: 'right' }
      bRow++
    })

    // Footer
    bRow += 2
    wsSummary.mergeCells(`B${bRow}:G${bRow}`)
    const footerCell = wsSummary.getRow(bRow).getCell(2)
    footerCell.value = `QuiCare Srl \u2014 Documento riservato \u2014 Generato automaticamente il ${today}`
    footerCell.font = { size: 8, italic: true, color: { argb: 'FF94A3B8' } }
    footerCell.alignment = { horizontal: 'center' }

    wsSummary.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }

    // ══════════════════════════════════════════════════════════════
    // FOGLIO 2: DETTAGLIO PRESTAZIONI
    // ══════════════════════════════════════════════════════════════
    const wsPrest = wb.addWorksheet('Dettaglio Prestazioni', { properties: { tabColor: { argb: 'FF4F46E5' } } })
    wsPrest.views = [{ showGridLines: false, zoomScale: 90 }]
    const pCols = 12
    const pWidths = [5, 14, 26, 22, 12, 14, 10, 14, 14, 14, 30, 14]
    pWidths.forEach((w, i) => { wsPrest.getColumn(i + 1).width = w })

    // Title banner
    wsPrest.mergeCells(`A1:L1`)
    wsPrest.getRow(1).height = 40
    const pTitleCell = wsPrest.getRow(1).getCell(1)
    pTitleCell.value = `DETTAGLIO PRESTAZIONI \u2014 ${nomeCompleto} \u2014 ${periodo}`
    pTitleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
    pTitleCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsPrest, 1, pCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } } })

    // Info ribbon
    wsPrest.mergeCells(`A2:L2`)
    wsPrest.getRow(2).height = 24
    const pInfoCell = wsPrest.getRow(2).getCell(1)
    pInfoCell.value = `${tipoLabel}  \u00b7  ${accesses.length} prestazioni  \u00b7  Subtotale \u20ac${billing.subtotale.toFixed(2)}  \u00b7  ${isPiva ? `ENPAPI \u20ac${billing.enpapi4.toFixed(2)}` : `Ritenuta \u20ac${billing.ritenuta20.toFixed(2)}`}  \u00b7  Generato il ${today}`
    pInfoCell.font = { size: 9, italic: true, color: { argb: 'FF312E81' } }
    pInfoCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsPrest, 2, pCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } } })

    // Headers
    const detailHeaders = ['#', 'Data', 'Tipo', 'Descrizione', 'Q.t\u00e0/Ore', 'Importo', 'Km', 'Rimb. Km', 'Extra', isPiva ? 'ENPAPI 4%' : 'Ritenuta 20%', 'Note', 'TOTALE']
    wsPrest.getRow(3).height = 28
    detailHeaders.forEach((h, i) => {
      const c = wsPrest.getRow(3).getCell(i + 1)
      c.value = h
      c.font = { size: 9, bold: true, color: { argb: 'FFFFFFFF' } }
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } }
      c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
      c.border = { bottom: { style: 'medium', color: { argb: 'FF0F172A' } } }
    })

    // Data rows
    const sortedAccesses = [...accesses].sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
    let pr = 4
    sortedAccesses.forEach((a, i) => {
      const row = wsPrest.getRow(pr)
      row.height = 22
      const imp = getImporto(a)
      const fiscal = isPiva ? imp * 0.04 : imp * 0.20
      const total = isPiva ? (imp * 1.04 + (a.rimborso_km || 0) + (a.rimborso_extra || 0)) : (imp * 0.80 + (a.rimborso_km || 0) + (a.rimborso_extra || 0))
      const emoji = TIPO_EMOJIS[a.tipo] || ''
      const vals: any[] = [
        i + 1,
        new Date(a.data).toLocaleDateString('it-IT'),
        `${emoji} ${TIPO_LABELS[a.tipo] || a.tipo}`,
        a.descrizione || '-',
        `${a.ore || 1}h`,
        imp,
        a.km_percorsi || '',
        a.rimborso_km || '',
        a.rimborso_extra || '',
        fiscal,
        a.note || '',
        total,
      ]
      const bg = i % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF'
      vals.forEach((v, j) => {
        const c = row.getCell(j + 1)
        c.value = (typeof v === 'number' && v === 0) ? '' : v
        c.font = { size: 9, bold: j === 11, color: { argb: j === 11 ? 'FF1E1B4B' : 'FF334155' } }
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
        c.alignment = { vertical: 'middle', horizontal: j <= 3 || j === 10 ? 'left' : 'center' }
        c.border = { bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } } }
        if (typeof v === 'number' && v > 0 && [5, 7, 8, 9, 11].includes(j)) c.numFmt = '\u20ac #,##0.00'
      })
      pr++
    })

    // Totals row
    const totR = wsPrest.getRow(pr)
    totR.height = 28
    for (let c = 1; c <= pCols; c++) {
      const cell = totR.getCell(c)
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } }
      cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center' }
      cell.border = { top: { style: 'medium', color: { argb: 'FF1E1B4B' } } }
    }
    totR.getCell(1).value = 'TOTALE'
    totR.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' }
    totR.getCell(6).value = billing.subtotale; totR.getCell(6).numFmt = '\u20ac #,##0.00'
    totR.getCell(7).value = billing.totaleKm
    totR.getCell(8).value = billing.rimborsiKm; totR.getCell(8).numFmt = '\u20ac #,##0.00'
    totR.getCell(9).value = billing.rimborsiExtra; totR.getCell(9).numFmt = '\u20ac #,##0.00'
    totR.getCell(10).value = isPiva ? billing.enpapi4 : billing.ritenuta20; totR.getCell(10).numFmt = '\u20ac #,##0.00'
    totR.getCell(12).value = isPiva ? billing.totaleFatturare : billing.nettoRitenuta; totR.getCell(12).numFmt = '\u20ac #,##0.00'

    // Footer
    pr += 2
    wsPrest.mergeCells(`A${pr}:L${pr}`)
    const pFooter = wsPrest.getRow(pr).getCell(1)
    pFooter.value = `QuiCare Srl \u2014 Documento riservato \u2014 ${nomeCompleto} \u2014 ${periodo}`
    pFooter.font = { size: 8, italic: true, color: { argb: 'FF94A3B8' } }
    pFooter.alignment = { horizontal: 'center' }

    wsPrest.views = [{ state: 'frozen', xSplit: 0, ySplit: 3 }]
    wsPrest.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }

    // ══════════════════════════════════════════════════════════════
    // FOGLIO 3: TARIFFARIO
    // ══════════════════════════════════════════════════════════════
    const wsCosti = wb.addWorksheet('Tariffario', { properties: { tabColor: { argb: 'FF059669' } } })
    wsCosti.views = [{ showGridLines: false, zoomScale: 90 }]
    wsCosti.columns = [{ width: 4 }, { width: 34 }, { width: 18 }, { width: 16 }, { width: 4 }]
    const cCols = 5

    wsCosti.mergeCells('A1:E1')
    wsCosti.getRow(1).height = 40
    const cTitle = wsCosti.getRow(1).getCell(1)
    cTitle.value = 'TARIFFARIO PRESTAZIONI \u2014 QuiCare Srl'
    cTitle.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
    cTitle.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsCosti, 1, cCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } } })

    wsCosti.mergeCells('A2:E2')
    wsCosti.getRow(2).height = 22
    const cSubCell = wsCosti.getRow(2).getCell(1)
    cSubCell.value = `Aggiornato al ${today}  \u00b7  Tariffa km: \u20ac${(rates.tariffa_km || 0).toFixed(2)}/km`
    cSubCell.font = { size: 9, italic: true, color: { argb: 'FF065F46' } }
    cSubCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsCosti, 2, cCols, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } } })

    const cHeaders = ['', 'Prestazione', 'Tipo Tariffa', 'Costo']
    wsCosti.getRow(3).height = 26
    cHeaders.forEach((h, i) => {
      const c = wsCosti.getRow(3).getCell(i + 1)
      c.value = h
      c.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF065F46' } }
      c.alignment = { vertical: 'middle', horizontal: 'center' }
    })

    TIPI_PRESTAZIONE.forEach((tp, idx) => {
      const row = wsCosti.getRow(4 + idx)
      row.height = 28
      const emoji = TIPO_EMOJIS[tp.key] || ''
      const bg = idx % 2 === 0 ? 'FFD1FAE5' : 'FFFFFFFF'
      const vals = ['', `${emoji}  ${tp.label}`, 'Tariffa oraria (\u20ac/h)', `\u20ac ${rates[tp.key].toFixed(2)}`]
      vals.forEach((v, j) => {
        const c = row.getCell(j + 1)
        c.value = v
        c.font = j === 1 ? { size: 11, bold: true, color: { argb: 'FF1E293B' } } : { size: 10, color: { argb: 'FF475569' } }
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
        c.alignment = { vertical: 'middle', horizontal: j <= 1 ? 'left' : 'center' }
        c.border = { bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } } }
      })
    })

    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `QuiCare_Fatturazione_${nurse.cognome}_${nurse.nome}_${periodoFile}.xlsx`)
    setSuccess('\u2705 Excel Premium esportato \u2014 Resoconto + Dettaglio + Tariffario!')
  }


  async function handleExportPDF() {
    if (!nurse) return
    setSuccess('Esecuzione Audit A.I. per Export PDF in corso...');
    const doc: any = new jsPDF()
    const periodo = periodoLabel
    const today = new Date().toLocaleDateString('it-IT')
    const pageW = doc.internal.pageSize.width
    const pageH = doc.internal.pageSize.height
    const isPiva = !(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta')

    doc.setFillColor(49, 46, 129); doc.rect(0, 0, pageW, 32, 'F')
    doc.setFillColor(79, 70, 229); doc.rect(0, 0, pageW * 0.6, 32, 'F')
    doc.setTextColor(255); doc.setFontSize(16); doc.setFont('helvetica', 'bold')
    doc.text('FATTURA PROFORMA / NOTULA COMPENSI', 14, 14)
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.text(periodo, 14, 22)
    doc.setFontSize(10); doc.setFont('helvetica', 'bold')
    doc.text('QuiCare Srl', pageW - 14, 12, { align: 'right' })
    doc.setFontSize(8); doc.setFont('helvetica', 'normal')
    doc.text(`Generato il ${today}`, pageW - 14, 20, { align: 'right' })

    const infoY = 38
    doc.setFillColor(248, 250, 252); doc.roundedRect(14, infoY, pageW - 28, 16, 3, 3, 'F')
    doc.setDrawColor(226, 232, 240); doc.roundedRect(14, infoY, pageW - 28, 16, 3, 3, 'S')
    doc.setTextColor(30, 41, 59); doc.setFontSize(12); doc.setFont('helvetica', 'bold')
    doc.text(`${nurse.cognome} ${nurse.nome}`, 20, infoY + 9)
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139)
    doc.text(`${nurse.tipo_contratto || ''} | ${nurse.regione || ''} - ${nurse.provincia || ''}`, pageW - 20, infoY + 9, { align: 'right' })

    // KPI
    const kpiY = infoY + 22
    const kData = TIPI_PRESTAZIONE.map(t => ({
      label: t.label, value: `€${(billing.byTipo[t.key]?.totale || 0).toFixed(0)}`,
      sub: `${billing.byTipo[t.key]?.ore || 0}h · ${billing.byTipo[t.key]?.count || 0} acc.`
    }))
    const kW = (pageW - 28 - 9) / 4
    kData.forEach((k, i) => {
      const x = 14 + i * (kW + 3)
      doc.setFillColor(248, 250, 252); doc.roundedRect(x, kpiY, kW, 16, 2, 2, 'F')
      doc.setTextColor(79, 70, 229); doc.setFontSize(12); doc.setFont('helvetica', 'bold')
      doc.text(k.value, x + 5, kpiY + 7)
      doc.setTextColor(100, 116, 139); doc.setFontSize(6); doc.text(k.label, x + 5, kpiY + 12)
      doc.setTextColor(148, 163, 184); doc.text(k.sub, x + kW - 3, kpiY + 7, { align: 'right' })
    })

    // === AI EXECUTIVE SUMMARY AUDIT ===
    const aiSummaryText = await generateBillingSummary(nurse, billing, accesses, 'PDF');
    const aiY = kpiY + 20;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, aiY, pageW - 28, 22, 2, 2, 'FD');
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('✨ AUDIT MENSILE A.I.', 18, aiY + 6);
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(aiSummaryText, 18, aiY + 11, { maxWidth: pageW - 36, lineHeightFactor: 1.4 });

    // Table
    const tableData = accesses.map((a, i) => [
      i + 1, new Date(a.data).toLocaleDateString('it-IT'), TIPO_LABELS[a.tipo] || a.tipo,
      a.descrizione || '', a.ore || 1,
      `€${getImporto(a).toFixed(2)}`,
      (a.km_percorsi || 0) > 0 ? a.km_percorsi : '',
      (a.rimborso_km || 0) > 0 ? `€${a.rimborso_km.toFixed(2)}` : '',
      (a.rimborso_extra || 0) > 0 ? `€${a.rimborso_extra.toFixed(2)}` : '',
    ])
    autoTable(doc, {
      startY: aiY + 26,
      head: [['#', 'Data', 'Tipo', 'Descrizione', 'Q.tà/Ore', 'Importo', 'Km', 'Rimb. Km', 'Extra']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2, lineColor: [226, 232, 240], lineWidth: 0.2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    })

    // Summary boxes
    let summY = (doc as any).lastAutoTable.finalY + 8
    const boxW = (pageW - 28 - 6) / 3

    // Subtotale
    doc.setFillColor(248, 250, 252); doc.roundedRect(14, summY, boxW, 24, 3, 3, 'F')
    doc.setTextColor(71, 85, 105); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
    doc.text('SUBTOTALE', 18, summY + 6)
    doc.setFontSize(14); doc.setTextColor(30, 41, 59)
    doc.text(`\u20ac ${billing.subtotale.toFixed(2)}`, 18, summY + 16)

    const midX = 14 + boxW + 3
    const totX = midX + boxW + 3

    if (isPiva) {
      // ENPAPI 4%
      doc.setFillColor(240, 253, 244); doc.roundedRect(midX, summY, boxW, 24, 3, 3, 'F')
      doc.setTextColor(22, 101, 52); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
      doc.text('ENPAPI 4%', midX + 4, summY + 6)
      doc.setFontSize(14); doc.text(`\u20ac ${billing.enpapi4.toFixed(2)}`, midX + 4, summY + 16)
      // Totale fatturare
      doc.setFillColor(79, 70, 229); doc.roundedRect(totX, summY, boxW, 24, 3, 3, 'F')
      doc.setTextColor(255); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
      doc.text('TOTALE DA FATTURARE', totX + 4, summY + 6)
      doc.setFontSize(14); doc.text(`\u20ac ${billing.totaleFatturare.toFixed(2)}`, totX + 4, summY + 16)
    } else {
      // Ritenuta 20%
      doc.setFillColor(254, 242, 242); doc.roundedRect(midX, summY, boxW, 24, 3, 3, 'F')
      doc.setTextColor(153, 27, 27); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
      doc.text("RITENUTA D'ACCONTO 20%", midX + 4, summY + 6)
      doc.setFontSize(14); doc.text(`- \u20ac ${billing.ritenuta20.toFixed(2)}`, midX + 4, summY + 16)
      // Netto a pagare
      doc.setFillColor(79, 70, 229); doc.roundedRect(totX, summY, boxW, 24, 3, 3, 'F')
      doc.setTextColor(255); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
      doc.text('NETTO A PAGARE', totX + 4, summY + 6)
      doc.setFontSize(14); doc.text(`\u20ac ${billing.nettoRitenuta.toFixed(2)}`, totX + 4, summY + 16)
    }

    // Rimborsi row if applicable
    if (billing.rimborsiKm > 0 || billing.rimborsiExtra > 0) {
      summY += 28
      const halfW = (pageW - 28 - 3) / 2
      if (billing.rimborsiKm > 0) {
        doc.setFillColor(248, 250, 252); doc.roundedRect(14, summY, halfW, 18, 2, 2, 'F')
        doc.setTextColor(71, 85, 105); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
        doc.text(`RIMBORSO KM (${billing.totaleKm} km)`, 18, summY + 6)
        doc.setFontSize(12); doc.setTextColor(30, 41, 59)
        doc.text(`\u20ac ${billing.rimborsiKm.toFixed(2)}`, 18, summY + 14)
      }
      if (billing.rimborsiExtra > 0) {
        const exX = 14 + halfW + 3
        doc.setFillColor(248, 250, 252); doc.roundedRect(exX, summY, halfW, 18, 2, 2, 'F')
        doc.setTextColor(71, 85, 105); doc.setFontSize(7); doc.setFont('helvetica', 'bold')
        doc.text('EXTRA', exX + 4, summY + 6)
        doc.setFontSize(12); doc.setTextColor(30, 41, 59)
        doc.text(`\u20ac ${billing.rimborsiExtra.toFixed(2)}`, exX + 4, summY + 14)
      }
    }

    // Footer
    const pages = doc.getNumberOfPages()
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i); doc.setFontSize(7); doc.setTextColor(148, 163, 184)
      doc.text(`Pagina ${i}/${pages}`, 14, pageH - 8)
      doc.setFont('helvetica', 'bold')
      doc.text('GESTIONE INFERMIERI \u00a9 QuiCare Srl', pageW / 2, pageH - 8, { align: 'center' })
    }

    doc.save(`QuiCare_Fatturazione_${nurse.cognome}_${nurse.nome}_${periodo.replace(' ', '_')}.pdf`)
    setSuccess('\u2705 PDF esportato!')
  }

  if (isLoading) return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento...</div>

  return (
    <div className="space-y-6">
      {/* ═══ HEADER PREMIUM ═══ */}
      <div className="bg-gradient-to-r from-slate-800 via-indigo-900 to-purple-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, #818cf8 0%, transparent 50%), radial-gradient(circle at 80% 50%, #c084fc 0%, transparent 50%)'}} />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <Euro className="w-7 h-7 text-amber-300" />
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight">Fatturazione</h2>
              <p className="text-indigo-200 text-sm mt-0.5">Centro di gestione finanziaria</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle Mese / Periodo */}
            <button onClick={() => { setDateMode(d => d === 'month' ? 'range' : 'month') }} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${dateMode === 'range' ? 'bg-amber-400 text-amber-900' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
              <CalendarRange className="w-3.5 h-3.5" />
              {dateMode === 'range' ? 'Periodo' : 'Mese'}
            </button>
            {dateMode === 'month' ? (
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 px-1">
                <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded-lg transition-all"><ChevronLeft className="w-4 h-4" /></button>
                <div className="text-center min-w-[120px] py-1">
                  <div className="text-sm font-black">{MESI[selectedMonth]}</div>
                  <div className="text-xs text-indigo-300">{selectedYear}</div>
                </div>
                <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded-lg transition-all"><ChevronRight className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 px-3 py-1.5">
                <label className="text-xs text-indigo-300">Dal</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-transparent border-none text-white text-sm font-bold focus:outline-none w-[130px] [color-scheme:dark]" />
                <label className="text-xs text-indigo-300">Al</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-transparent border-none text-white text-sm font-bold focus:outline-none w-[130px] [color-scheme:dark]" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {error}<button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button></div>}
      {success && <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-700 text-sm"><Check className="w-4 h-4" /> {success}<button onClick={() => setSuccess('')} className="ml-auto"><X className="w-4 h-4" /></button></div>}

      {/* ═══ TAB NAVIGATION ═══ */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="flex border-b border-slate-200">
          {([
            { key: 'cruscotto', label: 'Cruscotto', icon: LayoutDashboard, desc: 'KPI e panoramica' },
            { key: 'compensi', label: 'Compensi', icon: Euro, desc: 'Dettaglio operatore' },
            { key: 'report', label: 'Report', icon: FileText, desc: 'Analisi globale' },
            { key: 'config', label: 'Configurazione', icon: Wrench, desc: 'Tariffe e parametri' },
          ] as const).map(tab => (
            <button key={tab.key} onClick={() => setBillingTab(tab.key)}
              className={`flex-1 py-4 px-3 flex flex-col items-center gap-1 transition-all relative
                ${billingTab === tab.key
                  ? 'text-indigo-600 bg-indigo-50/50'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}>
              <tab.icon className="w-5 h-5" />
              <span className="text-xs font-black uppercase tracking-wider">{tab.label}</span>
              {billingTab === tab.key && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-indigo-500 rounded-full" />}
            </button>
          ))}
        </div>

        {/* ═══ TAB: CRUSCOTTO ═══ */}
        {billingTab === 'cruscotto' && (
          <div className="p-6 space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Fatturato Lordo', value: `€${globalStats.fatturato.toFixed(0)}`, icon: TrendingUp, color: 'from-indigo-500 to-purple-600', textColor: 'text-white' },
                { label: 'Accessi Totali', value: globalStats.totalAccessi.toString(), icon: BarChart3, color: 'from-emerald-500 to-teal-600', textColor: 'text-white' },
                { label: 'Ore Lavorate', value: `${globalStats.totalOre.toFixed(0)}h`, icon: Clock, color: 'from-amber-500 to-orange-600', textColor: 'text-white' },
                { label: 'Media/Accesso', value: `€${globalStats.mediaAccesso.toFixed(0)}`, icon: PieChart, color: 'from-rose-500 to-pink-600', textColor: 'text-white' },
                { label: 'Operatori Attivi', value: globalStats.byNurse.length.toString(), icon: Users, color: 'from-sky-500 to-blue-600', textColor: 'text-white' },
              ].map(kpi => (
                <div key={kpi.label} className={`bg-gradient-to-br ${kpi.color} rounded-2xl p-4 ${kpi.textColor} shadow-lg relative overflow-hidden`}>
                  <div className="absolute top-2 right-2 opacity-20"><kpi.icon className="w-10 h-10" /></div>
                  <div className="text-2xl font-black">{kpi.value}</div>
                  <div className="text-xs font-bold opacity-80 mt-1 uppercase tracking-wider">{kpi.label}</div>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bar Chart: Per Tipo */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-500" /> Fatturato per Tipologia
                </h4>
                <div className="space-y-3">
                  {TIPI_PRESTAZIONE.map(tp => {
                    const data = globalStats.byTipo[tp.key] || { count: 0, ore: 0, totale: 0 }
                    const pct = globalStats.tipoTotal > 0 ? (data.totale / globalStats.tipoTotal) * 100 : 0
                    const colors: Record<string, string> = { hospital_meeting: 'bg-purple-500', primo_accesso: 'bg-blue-500', accessi_successivi: 'bg-emerald-500', training_farmaceutica: 'bg-amber-500' }
                    return (
                      <div key={tp.key}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-bold text-slate-600 flex items-center gap-1.5">
                            <span>{tp.icon}</span> {tp.label}
                          </span>
                          <span className="font-black text-slate-800">€{data.totale.toFixed(0)} <span className="text-slate-400 font-normal">({pct.toFixed(0)}%)</span></span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${colors[tp.key] || 'bg-slate-500'} rounded-full transition-all duration-700`} style={{width: `${Math.max(pct, 2)}%`}} />
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{data.count} accessi · {data.ore}h</div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Donut-style Breakdown */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-indigo-500" /> Ripartizione Percentuale
                </h4>
                <div className="flex items-center justify-center mb-4">
                  <svg viewBox="0 0 120 120" className="w-40 h-40">
                    {(() => {
                      const colors = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b']
                      let cumPct = 0
                      return TIPI_PRESTAZIONE.map((tp, i) => {
                        const data = globalStats.byTipo[tp.key] || { totale: 0 }
                        const pct = globalStats.tipoTotal > 0 ? (data.totale / globalStats.tipoTotal) * 100 : 0
                        const startAngle = cumPct * 3.6 - 90
                        cumPct += pct
                        const endAngle = cumPct * 3.6 - 90
                        if (pct === 0) return null
                        const r = 50, cx = 60, cy = 60
                        const x1 = cx + r * Math.cos(startAngle * Math.PI / 180)
                        const y1 = cy + r * Math.sin(startAngle * Math.PI / 180)
                        const x2 = cx + r * Math.cos(endAngle * Math.PI / 180)
                        const y2 = cy + r * Math.sin(endAngle * Math.PI / 180)
                        const large = pct > 50 ? 1 : 0
                        return <path key={tp.key} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={colors[i]} opacity={0.85} />
                      })
                    })()}
                    <circle cx="60" cy="60" r="25" fill="white" />
                    <text x="60" y="57" textAnchor="middle" className="text-[10px] font-bold fill-slate-800">{globalStats.totalAccessi}</text>
                    <text x="60" y="68" textAnchor="middle" className="text-[7px] fill-slate-400">accessi</text>
                  </svg>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {TIPI_PRESTAZIONE.map((tp, i) => {
                    const colors = ['bg-purple-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500']
                    const data = globalStats.byTipo[tp.key] || { totale: 0 }
                    const pct = globalStats.tipoTotal > 0 ? (data.totale / globalStats.tipoTotal) * 100 : 0
                    return (
                      <div key={tp.key} className="flex items-center gap-2 text-xs">
                        <div className={`w-2.5 h-2.5 rounded-full ${colors[i]}`} />
                        <span className="text-slate-600 truncate">{tp.label}</span>
                        <span className="ml-auto font-black text-slate-800">{pct.toFixed(0)}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Top Operatori */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-500" /> Classifica Operatori — {MESI[selectedMonth]} {selectedYear}
                </h4>
              </div>
              {globalStats.byNurse.length === 0 ? (
                <div className="p-8 text-center text-slate-300 text-sm">Nessun accesso registrato questo mese</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white border-b border-slate-200 text-left text-xs font-bold text-slate-400 uppercase">
                      <th className="px-5 py-3 w-10">#</th>
                      <th className="px-5 py-3">Operatore</th>
                      <th className="px-5 py-3">Contratto</th>
                      <th className="px-5 py-3 text-center">Accessi</th>
                      <th className="px-5 py-3 text-center">Ore</th>
                      <th className="px-5 py-3 text-right">Compenso</th>
                      <th className="px-5 py-3 text-right">Rimborsi</th>
                      <th className="px-5 py-3 text-right font-black">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalStats.byNurse.map((n, i) => {
                      const totale = n.subtotale + n.rimborsi
                      const finale = n.isPiva ? n.subtotale * 1.04 + n.rimborsi : n.subtotale * 0.80 + n.rimborsi
                      const medals = ['🥇', '🥈', '🥉']
                      return (
                        <tr key={n.nurse.id} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors cursor-pointer"
                          onClick={() => { setSelectedNurse(n.nurse.id); setBillingTab('compensi') }}>
                          <td className="px-5 py-3.5 text-lg">{medals[i] || <span className="text-sm text-slate-400">{i + 1}</span>}</td>
                          <td className="px-5 py-3.5 font-bold text-slate-800 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center text-xs font-black">{n.nurse.nome[0]}{n.nurse.cognome[0]}</div>
                            {n.nurse.cognome} {n.nurse.nome}
                          </td>
                          <td className="px-5 py-3.5"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${n.isPiva ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>{n.isPiva ? 'P.IVA' : 'Ritenuta'}</span></td>
                          <td className="px-5 py-3.5 text-center font-bold text-slate-600">{n.count}</td>
                          <td className="px-5 py-3.5 text-center text-slate-600">{n.ore}h</td>
                          <td className="px-5 py-3.5 text-right text-slate-600">€{n.subtotale.toFixed(2)}</td>
                          <td className="px-5 py-3.5 text-right text-slate-400">€{n.rimborsi.toFixed(2)}</td>
                          <td className="px-5 py-3.5 text-right font-black text-slate-800">€{finale.toFixed(2)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ═══ TAB: COMPENSI ═══ */}
        {billingTab === 'compensi' && (
          <div className="p-6 space-y-6">
            {/* Operator selector */}
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Operatore</label>
                <select value={selectedNurse} onChange={e => setSelectedNurse(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">Seleziona operatore...</option>
                  {nurses.map(n => <option key={n.id} value={n.id}>{n.cognome} {n.nome} — {n.regione}</option>)}
                </select>
              </div>
              {isAdmin && selectedNurse && (
                <button onClick={() => { setFormData({ ...emptyForm, costo_orario: rates.primo_accesso }); setShowForm(true) }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm">
                  <Plus className="w-4 h-4" /> Nuova Prestazione
                </button>
              )}
            </div>

            {!selectedNurse ? (
              <div className="text-center py-12 text-slate-300"><Users className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">Seleziona un operatore per vedere il dettaglio compensi</p></div>
            ) : (
              <>
                {/* Nurse Header Card */}
                {nurse && (
                  <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-2xl border border-indigo-100 p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center text-xl font-black shadow-lg">{nurse.nome[0]}{nurse.cognome[0]}</div>
                      <div>
                        <div className="text-lg font-black text-slate-800">{nurse.cognome} {nurse.nome}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${!(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta') ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                            {!(nurse.tipo_contratto || '').toLowerCase().includes('ritenuta') ? 'P.IVA' : "Ritenuta d'Acconto"}
                          </span>
                          <span className="text-xs text-slate-400">{nurse.regione} · {nurse.provincia}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div><div className="text-2xl font-black text-indigo-600">€{billing.subtotale.toFixed(0)}</div><div className="text-[10px] font-bold text-slate-400 uppercase">Compenso</div></div>
                      <div><div className="text-2xl font-black text-emerald-600">{billing.totaleAccessi}</div><div className="text-[10px] font-bold text-slate-400 uppercase">Accessi</div></div>
                    </div>
                  </div>
                )}

                {/* KPI Cards per tipo */}
                {accesses.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {TIPI_PRESTAZIONE.map(tp => {
                const stats = billing.byTipo[tp.key]
                const cc = CARD_COLORS[tp.key]
                const Icon = TIPO_ICONS[tp.key]
                return (
                  <div key={tp.key} className={`bg-gradient-to-br ${cc.bg} rounded-2xl border ${cc.border} p-4 relative overflow-hidden`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg">{tp.icon}</span>
                      <Icon className={`w-4 h-4 ${cc.icon}`} />
                    </div>
                    <div className={`text-2xl font-black ${cc.text}`}>€{stats.totale.toFixed(0)}</div>
                    <div className="text-xs text-slate-500 font-bold mt-1">{tp.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {stats.ore}h × €{rates[tp.key]}/h · {stats.count} acc.
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Totali — adattato al tipo contratto */}
          {accesses.length > 0 && (() => {
            const isPiva = !(nurse?.tipo_contratto || '').toLowerCase().includes('ritenuta')
            return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="text-xs font-bold text-slate-400 uppercase">Subtotale Prestazioni</div>
                <div className="text-3xl font-black text-slate-800 mt-1">€{billing.subtotale.toFixed(2)}</div>
                {billing.rimborsiKm > 0 && <div className="text-xs text-slate-400 mt-1">+ €{billing.rimborsiKm.toFixed(2)} rimborsi km ({billing.totaleKm} km)</div>}
                {billing.rimborsiExtra > 0 && <div className="text-xs text-slate-400">+ €{billing.rimborsiExtra.toFixed(2)} extra</div>}
              </div>
              {isPiva ? (
                <>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-200 p-5">
                    <div className="text-xs font-bold text-emerald-600 uppercase">ENPAPI 4%</div>
                    <div className="text-3xl font-black text-emerald-700 mt-1">€{billing.enpapi4.toFixed(2)}</div>
                    <div className="text-xs text-emerald-500 mt-1">4% su €{billing.baseCompute.toFixed(2)}</div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white">
                    <div className="text-xs font-bold text-indigo-200 uppercase">Totale da Fatturare</div>
                    <div className="text-3xl font-black mt-1">€{billing.totaleFatturare.toFixed(2)}</div>
                    <div className="text-xs text-indigo-200 mt-1">Subtotale + ENPAPI 4%</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl border border-red-200 p-5">
                    <div className="text-xs font-bold text-red-600 uppercase">Ritenuta d'Acconto 20%</div>
                    <div className="text-3xl font-black text-red-700 mt-1">- €{billing.ritenuta20.toFixed(2)}</div>
                    <div className="text-xs text-red-500 mt-1">20% su €{billing.baseCompute.toFixed(2)}</div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white">
                    <div className="text-xs font-bold text-indigo-200 uppercase">Netto a Pagare</div>
                    <div className="text-3xl font-black mt-1">€{billing.nettoRitenuta.toFixed(2)}</div>
                    <div className="text-xs text-indigo-200 mt-1">Subtotale - Ritenuta 20%</div>
                  </div>
                </>
              )}
            </div>
            )
          })()}

          {/* Export */}
          {accesses.length > 0 && (
            <div className="flex gap-2">
              <button onClick={handleExportExcel} className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all">
                <FileSpreadsheet className="w-4 h-4" /> Esporta Excel
              </button>
              <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition-all">
                <Download className="w-4 h-4" /> Esporta PDF
              </button>
            </div>
          )}

          {/* Access list */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-700">Prestazioni di {nurse?.cognome} {nurse?.nome} — {periodoLabel}</h3>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca prestazione..." className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-[200px]" />
            </div>
            {accesses.length === 0 ? (
              <div className="text-center py-12 text-slate-300"><Euro className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">Nessuna prestazione registrata per questo mese</p></div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Data</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Tipo</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Descrizione</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Q.tà/Ore</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Importo</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Km</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Rimb. Km</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Extra</th>
                    <th className="text-left px-4 py-3 font-bold text-slate-500 text-xs uppercase">Note</th>
                    {isAdmin && <th className="text-right px-4 py-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredAccesses.filter(a => showTrash ? true : !a.cestino).map(a => {
                    const Icon = TIPO_ICONS[a.tipo] || Home
                    const tp = TIPI_PRESTAZIONE.find(t => t.key === a.tipo)
                    return (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-bold text-slate-700">{new Date(a.data).toLocaleDateString('it-IT')}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold border ${TIPO_STYLES[a.tipo] || 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                            <Icon className="w-3 h-3" /> {TIPO_LABELS[a.tipo] || a.tipo}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs max-w-[150px] truncate">{a.descrizione || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{a.ore || 1}h</td>
                        <td className="px-4 py-3 font-bold text-slate-800">€{getImporto(a).toFixed(2)}</td>
                        <td className="px-4 py-3 text-slate-600">{(a.km_percorsi || 0) > 0 ? `${a.km_percorsi} km` : '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{(a.rimborso_km || 0) > 0 ? `€${(a.rimborso_km).toFixed(2)}` : '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{(a.rimborso_extra || 0) > 0 ? `€${(a.rimborso_extra).toFixed(2)}` : '-'}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-[120px] truncate">{a.note || '-'}</td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                            {a.cestino ? (
                              <button onClick={async () => {
                                if (confirm('Ripristinare questo accesso?')) {
                                  await supabase.from('accesses').update({ cestino: false }).eq('id', a.id);
                                  fetchBillingData();
                                }
                              }} className="px-2 py-1 bg-slate-800 text-white rounded-lg transition-colors text-[10px] font-bold" title="Ripristina">
                                Ripristina
                              </button>
                            ) : (
                              <button onClick={async () => {
                                if(confirm('Spostare nel cestino?')) {
                                  await supabase.from('accesses').update({ cestino: true }).eq('id', a.id);
                                  fetchBillingData();
                                }
                              }} className="p-1.5 hover:bg-orange-50 rounded-lg text-slate-400 hover:text-orange-500" title="Sposta in Cestino">
                                <Archive className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => { if(confirm('Eliminare definitivamente questo accesso?')) handleDeleteAccess(a.id) }} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
              </>
            )}
          </div>
        )}

        {/* ═══ TAB: REPORT ═══ */}
        {billingTab === 'report' && (
          <div className="p-6 space-y-6">
            <GlobalBillingOverview accesses={accesses} nurses={nurses} rates={rates} month={selectedMonth} year={selectedYear} dateMode={dateMode} dateFrom={dateFrom} dateTo={dateTo} onSelectNurse={(id: string) => { setSelectedNurse(id); setBillingTab('compensi') }} />
          </div>
        )}

        {/* ═══ TAB: CONFIG ═══ */}
        {billingTab === 'config' && (
          <div className="p-6 space-y-6">
            <div className="max-w-xl mx-auto">
              <h3 className="text-lg font-black text-slate-800 mb-1">⚙️ Tariffe Prestazioni</h3>
              <p className="text-sm text-slate-400 mb-6">Configura le tariffe orarie per ogni tipo di prestazione</p>
              <div className="space-y-3">
                {TIPI_PRESTAZIONE.map(tp => (
                  <div key={tp.key} className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className="text-2xl">{tp.icon}</span>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-700">{tp.label}</div>
                      <div className="text-xs text-slate-400">Tariffa oraria (€/h)</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-500">€</span>
                      <input type="number" min={0} value={rates[tp.key]}
                        onChange={e => setRates({ ...rates, [tp.key]: Number(e.target.value) })}
                        className="w-24 px-3 py-2 border border-slate-200 rounded-xl text-sm text-right font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      <span className="text-sm text-slate-400">/h</span>
                    </div>
                  </div>
                ))}
                <hr className="border-slate-200 my-2" />
                <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <span className="text-2xl">🚗</span>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-700">Tariffa Chilometrica</div>
                    <div className="text-xs text-slate-400">Rimborso per km percorso</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-500">€</span>
                    <input type="number" min={0} step={0.01} value={rates.tariffa_km || 0}
                      onChange={e => setRates({ ...rates, tariffa_km: Number(e.target.value) })}
                      className="w-24 px-3 py-2 border border-slate-200 rounded-xl text-sm text-right font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <span className="text-sm text-slate-400">/km</span>
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 mt-4">
                  <div className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-2">Parametri Fiscali</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">ENPAPI (P.IVA):</span> <span className="font-bold text-slate-800">4%</span></div>
                    <div><span className="text-slate-500">Ritenuta d'Acconto:</span> <span className="font-bold text-slate-800">20%</span></div>
                  </div>
                </div>
                <button onClick={() => saveRates(rates)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md hover:shadow-lg transition-all mt-4">
                  <Check className="w-4 h-4" /> Salva Configurazione
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New access form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-black text-slate-800">Nuova Prestazione</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Tipo Prestazione</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIPI_PRESTAZIONE.map(tp => {
                    const active = formData.tipo === tp.key
                    const Icon = TIPO_ICONS[tp.key]
                    return (
                      <button key={tp.key} onClick={() => handleTipoChange(tp.key)}
                        className={`p-3 rounded-xl border-2 text-sm font-bold flex items-center gap-2 transition-all ${active
                          ? `${TIPO_STYLES[tp.key]} border-current` : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        <Icon className="w-4 h-4" /> {tp.label}
                        <span className="ml-auto text-[10px] opacity-60">€{rates[tp.key]}/h</span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Data</label>
                  <input type="date" value={formData.data || ''} onChange={e => setFormData({ ...formData, data: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Ore</label>
                  <input type="number" min={0.5} step={0.5} value={formData.ore || 1}
                    onChange={e => setFormData({ ...formData, ore: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Descrizione</label>
                <input type="text" value={formData.descrizione || ''} onChange={e => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Es: Training con az. Farmaceuti, Visita domiciliare paziente Rossi..."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Tariffa €/ora</label>
                  <input type="number" min={0} value={formData.costo_orario || 0}
                    onChange={e => setFormData({ ...formData, costo_orario: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Extra €</label>
                  <input type="number" min={0} step={0.01} value={formData.rimborso_extra || 0}
                    onChange={e => setFormData({ ...formData, rimborso_extra: Number(e.target.value) })}
                    placeholder="Rimborsi extra"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Km Percorsi</label>
                  <input type="number" min={0} step={1} value={formData.km_percorsi || 0}
                    onChange={e => {
                      const km = Number(e.target.value)
                      setFormData({ ...formData, km_percorsi: km, rimborso_km: Math.round(km * (rates.tariffa_km || 0) * 100) / 100 })
                    }}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Rimborso Km € <span className="text-slate-400 font-normal">(auto: {(formData.km_percorsi || 0)} × €{(rates.tariffa_km || 0).toFixed(2)})</span></label>
                  <input type="number" min={0} step={0.01} value={formData.rimborso_km || 0}
                    onChange={e => setFormData({ ...formData, rimborso_km: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Note</label>
                <input type="text" value={formData.note || ''} onChange={e => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note aggiuntive..." className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
              </div>
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                <div className="text-xs font-bold text-amber-600 uppercase mb-1">Anteprima Importo</div>
                <div className="text-lg font-black text-amber-800">
                  €{((formData.ore || 1) * (formData.costo_orario || 0)).toFixed(2)}
                  <span className="text-sm font-normal text-amber-600 ml-1">({formData.ore || 1}h × €{formData.costo_orario || 0}/h)</span>
                  {(formData.rimborso_km || 0) > 0 && <span className="text-sm font-normal text-amber-600"> + €{(formData.rimborso_km || 0).toFixed(2)} km ({formData.km_percorsi || 0}km)</span>}
                  {(formData.rimborso_extra || 0) > 0 && <span className="text-sm font-normal text-amber-600"> + €{(formData.rimborso_extra || 0).toFixed(2)} extra</span>}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2.5 text-sm font-bold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200">Annulla</button>
              <button onClick={handleSaveAccess} disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl shadow-md hover:shadow-lg disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? 'Salvataggio...' : 'Registra'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

